import ArticleLayout from '../../components/ArticleLayout';
import FjernvarmeKredsFigure from '../../components/figures/FjernvarmeKredsFigure';

export default function FjernvarmeRadiatorVirkerIkke() {
  return (
    <ArticleLayout
      title="Fjernvarme og radiatorer: Afkøling, returtemperatur og drift"
      metaTitle="Fjernvarme radiator virker ikke? Afkøling og retur"
      metaDescription="Virker din fjernvarmeradiator ikke? Forstå princippet om god afkøling, lav returtemperatur og hvorfor bunden af radiatoren altid skal være kold."
      canonicalPath="/fjernvarme-radiator-virker-ikke"
      introLead="På fjernvarmeanlæg er radiatorerne konstrueret til at udnytte varmeenergien i vandet så grundigt, at returvandet forlader boligen afkølet."
      introAnswer="Hvis din fjernvarmeradiator er kold i bunden, er det som udgangspunkt et tegn på korrekt drift – ikke en fejl. Problemet opstår først, hvis radiatoren enten er helt kold overalt og rummet ikke opvarmes, eller omvendt er brandvarm hele vejen igennem uden tilstrækkelig afkøling."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Fejlsøgning på fjernvarmeradiator' },
        { id: 'afkoeling', title: '2. Hvorfor afkøling er afgørende for varmeregningen' },
        { id: 'direkte-indirekte', title: '3. Direkte vs. indirekte fjernvarme' },
        { id: 'termostater-flere', title: '4. Indstil alle termostater i rummet ens' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 ting du skal kontrollere på fjernvarme"
      checklist={[
        {
          title: 'Tjek om radiatorer i samme rum er indstillet ens',
          description: 'Flere radiatorer i samme stue skal stå på nøjagtig samme trin, så de deler belastningen og afkøler ensartet.',
        },
        {
          title: 'Mærk om returrøret i bunden af radiatoren er køligt',
          description: 'Returrøret skal føles håndvarmt eller køligt (under ca. 30–35 °C) ved balanceret drift.',
        },
        {
          title: 'Undersøg om varmeværket har driftsforstyrrelser',
          description: 'Tjek forsyningsselskabets driftsside, hvis samtlige radiatorer og brugsvandet pludselig mister varmen.',
        },
        {
          title: 'Kontrollér differenstrykregulatoren i fjernvarmeunitten',
          description: 'Et kraftigt fald i gennemstrømning kan skyldes et tilstoppet snavssamler-filter i din fjernvarmeunit.',
        },
        {
          title: 'Sørg for mindst 20 cm frit rum foran alle radiatorflader',
          description: 'Fjernvarmeradiatorer afhænger af fri luftcirkulation for at kunne levere fuld afkøling uden overforbrug.',
        },
      ]}
      diagram={<FjernvarmeKredsFigure />}
      sections={[
        {
          id: 'afkoeling',
          title: 'Hvorfor afkøling er afgørende for varmeregningen',
          content: (
            <>
              <p>
                Fjernvarmeselskaber opkræver ofte en såkaldt motivations- eller afkølingstarif. Hvis dit hus sender vandet tilbage med for høj temperatur – fx <span className="font-mono">45 °C</span> i stedet for under <span className="font-mono">30–35 °C</span> – udnytter du ikke varmen ordentligt, og værket skal pumpe unødigt store vandmængder rundt.
              </p>
              <p>
                For at opnå god afkøling skal vandet bevæge sig langsomt igennem radiatoren. Radiatoren skal derfor være stor nok og forindstillet, så kun en passende mængde vand passerer igennem. Resultatet er netop en radiator, der er varm i toppen og kold i bunden.
              </p>
            </>
          ),
        },
        {
          id: 'direkte-indirekte',
          title: 'Direkte versus indirekte fjernvarmeanlæg',
          content: (
            <>
              <p>
                Der findes to grundlæggende typer fjernvarmeinstallationer i danske boliger:
              </p>
              <p>
                <strong>Direkte anlæg:</strong> Fjernvarmevandet fra gaden ledes direkte ind i dine radiatorer. Her reguleres trykket af værkets forsyning, og du skal aldrig efterfylde vand på radiatorkredsen. Lækager skal afbrydes øjeblikkeligt ved hovedhanerne.
              </p>
              <p>
                <strong>Indirekte anlæg:</strong> Der er indskudt en varmeveksler mellem fjernvarmerørene i gaden og dit interne radiatorsystem. Dit interne system er lukket, har eget manometer og en ekspansionsbeholder, og skal holdes på typisk <span className="font-mono">1,5 bar</span>.
              </p>
            </>
          ),
        },
        {
          id: 'termostater-flere',
          title: 'Den klassiske fejl: Kun at åbne én radiator i et stort rum',
          content: (
            <>
              <p>
                En udbredt misforståelse er at tro, at man sparer energi ved at lade én radiator i stuen køre for fuld skrue på trin 5, mens de to andre holdes slukkede på 0.
              </p>
              <p>
                Ifølge HOFOR og energirådgivere er dette en af de dyreste fejl: Den ene overbelastede radiator vil lade vandet suse igennem med høj hastighed uden afkøling. Bunden bliver brandvarm, returtemperaturen stiger katastrofalt, og varmeøkonomien ødelægges. Indstil altid alle radiatorer i samme rum på samme trin, fx 3.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal VVS-fagmand eller fjernvarmeselskab kontaktes?',
        content: (
          <p>
            Hvis fjernvarmemåleren viser en gennemsnitlig returtemperatur over 40–45 °C trods korrekte termostatindstillinger, eller hvis snavssamleren i fjernvarmeunitten er tilstoppet, bør du bestille et servicetjek hos en autoriseret VVS-installatør. Arbejde på fjernvarmeunit, trykventiler og hovedledninger må aldrig udføres af uautoriserede personer.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvad er en god returtemperatur på fjernvarme?',
          a: 'En god returtemperatur ligger typisk under 30–35 °C i nyere eller renoverede installationer. Jo lavere returtemperatur, desto mere effektivt har dine radiatorer optaget varmen.',
        },
        {
          q: 'Hvorfor straffes man for dårlig afkøling på fjernvarme?',
          a: 'Hvis for mange boliger sender varmt vand retur, taber forsyningsnettet store mængder energi i transmissionsrørene under jorden. Fjernvarmeselskaberne opkræver derfor et gebyr for at tilskynde korrekt afkøling.',
        },
        {
          q: 'Kan jeg selv rense snavssamleren i min fjernvarmeunit?',
          a: 'Rensning af snavssamler på fjernvarmesiden indebærer risiko for skoldning med hedt vand under tryk. Dette arbejde bør udføres af en autoriseret fagmand eller ved et regelmæssigt varmetjek.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'tjeklisten til kolde radiatorer' },
        { to: '/radiator-kold-i-bunden', text: 'Radiator kold i bunden', desc: 'hvorfor kold bund er bevis på god afkøling' },
        { to: '/spar-paa-varmen', text: 'Spar på varmen med fjernvarme', desc: 'ensartet indstilling og gode vaner' },
        { to: '/radiator-returventil', text: 'Radiator returventil og forindstilling', desc: 'sådan begrænses flowet' },
      ]}
      sources={[
        { url: 'https://kredslob.dk/privat/fjernvarme/returtemperatur', label: 'Få styr på returtemperaturen og spar på varmeregningen Kredsløb' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/saadan-faar-du-mest-ud-af-fjernvarmen', label: 'Sådan får du mest ud af fjernvarme | SparEnergi' },
        { url: 'https://www.hofor.dk/erhverv/fjernvarme/spoergsmaal-svar-fjernvarme-erhverv/fjernvarmen-fungerer-ejendommens-radiatorer-ikke-varme-goer-vi/', label: 'Fjernvarmen fungerer, men ejendommens radiatorer er ikke varme - HOFOR' },
      ]}
    />
  );
}
